export type StrategyState = 'GENERATED' | 'SURFACED' | 'ADOPTED' | 'ENABLED' | 'DISABLED';

export interface StateTransition {
  from: StrategyState | null;
  to: StrategyState;
  timestamp: string;
  actor: string;
}

export interface Strategy {
  strategyId: string;
  campaignId: string;
  currentState: StrategyState;
  strategyType: string;
  createdAt: string;
  transitions: StateTransition[];
}

export interface LifecycleData {
  advertiserId: string;
  totalStrategies: number;
  distribution: Record<StrategyState, number>;
  strategies: Strategy[];
}

const MOCK_STRATEGIES: Strategy[] = [
  {
    strategyId: 'STR-001',
    campaignId: 'C001',
    currentState: 'ENABLED',
    strategyType: 'KEYWORD_TARGETING',
    createdAt: '2026-04-20T10:00:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-20T10:00:00Z', actor: 'bedrock-agent' },
      { from: 'GENERATED', to: 'SURFACED', timestamp: '2026-04-21T08:30:00Z', actor: 'cs3-service' },
      { from: 'SURFACED', to: 'ADOPTED', timestamp: '2026-04-22T14:15:00Z', actor: 'advertiser' },
      { from: 'ADOPTED', to: 'ENABLED', timestamp: '2026-04-23T09:00:00Z', actor: 'campaign-manager' },
    ],
  },
  {
    strategyId: 'STR-002',
    campaignId: 'C001',
    currentState: 'SURFACED',
    strategyType: 'AUDIENCE_TARGETING',
    createdAt: '2026-04-25T08:15:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-25T08:15:00Z', actor: 'bedrock-agent' },
      { from: 'GENERATED', to: 'SURFACED', timestamp: '2026-04-26T10:00:00Z', actor: 'cs3-service' },
    ],
  },
  {
    strategyId: 'STR-003',
    campaignId: 'C002',
    currentState: 'DISABLED',
    strategyType: 'PRODUCT_TARGETING',
    createdAt: '2026-04-18T16:00:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-18T16:00:00Z', actor: 'rule-engine' },
      { from: 'GENERATED', to: 'SURFACED', timestamp: '2026-04-19T09:00:00Z', actor: 'cs3-service' },
      { from: 'SURFACED', to: 'ADOPTED', timestamp: '2026-04-20T11:30:00Z', actor: 'advertiser' },
      { from: 'ADOPTED', to: 'ENABLED', timestamp: '2026-04-21T08:00:00Z', actor: 'campaign-manager' },
      { from: 'ENABLED', to: 'DISABLED', timestamp: '2026-04-28T15:45:00Z', actor: 'advertiser' },
    ],
  },
  {
    strategyId: 'STR-004',
    campaignId: 'C002',
    currentState: 'GENERATED',
    strategyType: 'BID_OPTIMIZATION',
    createdAt: '2026-04-30T08:00:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-30T08:00:00Z', actor: 'bedrock-agent' },
    ],
  },
  {
    strategyId: 'STR-005',
    campaignId: 'C003',
    currentState: 'ADOPTED',
    strategyType: 'KEYWORD_TARGETING',
    createdAt: '2026-04-22T12:00:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-22T12:00:00Z', actor: 'bedrock-agent' },
      { from: 'GENERATED', to: 'SURFACED', timestamp: '2026-04-23T07:45:00Z', actor: 'cs3-service' },
      { from: 'SURFACED', to: 'ADOPTED', timestamp: '2026-04-25T16:20:00Z', actor: 'advertiser' },
    ],
  },
  {
    strategyId: 'STR-006',
    campaignId: 'C003',
    currentState: 'ENABLED',
    strategyType: 'AUDIENCE_TARGETING',
    createdAt: '2026-04-19T14:30:00Z',
    transitions: [
      { from: null, to: 'GENERATED', timestamp: '2026-04-19T14:30:00Z', actor: 'rule-engine' },
      { from: 'GENERATED', to: 'SURFACED', timestamp: '2026-04-20T09:15:00Z', actor: 'cs3-service' },
      { from: 'SURFACED', to: 'ADOPTED', timestamp: '2026-04-21T13:00:00Z', actor: 'advertiser' },
      { from: 'ADOPTED', to: 'ENABLED', timestamp: '2026-04-22T08:30:00Z', actor: 'campaign-manager' },
    ],
  },
];

export async function fetchStrategyLifecycle(
  advertiserId: string,
  _env: 'Beta' | 'Prod' = 'Beta',
): Promise<LifecycleData> {
  await new Promise((r) => setTimeout(r, 400 + Math.random() * 300));

  const strategies = MOCK_STRATEGIES;
  const distribution: Record<StrategyState, number> = {
    GENERATED: 0,
    SURFACED: 0,
    ADOPTED: 0,
    ENABLED: 0,
    DISABLED: 0,
  };
  for (const s of strategies) {
    distribution[s.currentState]++;
  }

  return {
    advertiserId,
    totalStrategies: strategies.length,
    distribution,
    strategies,
  };
}

export const STATE_COLORS: Record<StrategyState, string> = {
  GENERATED: '#A855F7',
  SURFACED: '#38BDF8',
  ADOPTED: '#FBBF24',
  ENABLED: '#4ADE80',
  DISABLED: '#F87171',
};
